import clsx from 'clsx';

import useAccountQuery from 'api/queries/useAccountQuery';
import useSelectionsQuery from 'api/queries/useSelectionsQuery';
import Progress from 'honeydew/Progress';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook/useAccountPlusMembershipHook';
import { filterPerkSelections, getFrozenItems } from 'utils/orderUtils';
import { getFrozenFeeAmountRemaining, getProductsSubtotal, getWaiveFrozenFee, PriceType } from 'utils/priceUtils';

/**
 * Shows progress toward the frozen-fee waive threshold, or a waived message when the threshold is met.
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.isFrozenAisleRoute=false] - When true, render even with no frozen products in the cart
 * @param {boolean} [props.stacked=false] - When true, adds styling for stacked layout contexts
 * @returns {JSX.Element|null}
 */
function FrozenFeeWaiveProgress({ isFrozenAisleRoute = false, stacked = false }) {
    const { data: account } = useAccountQuery();
    const { data: selections } = useSelectionsQuery();
    const { plusMembershipEligibleOrder } = useAccountPlusMembershipHook();

    if (!account || !selections) return null;

    const { frozenFeeWaiveThreshold } = account.nextOrder?.cartExperience ?? {};
    if (frozenFeeWaiveThreshold == null) {
        return null;
    }

    const filteredSelections = filterPerkSelections(selections);
    const frozenProducts = getFrozenItems(filteredSelections);
    if (frozenProducts.length === 0 && !isFrozenAisleRoute) {
        return null;
    }

    const priceType = plusMembershipEligibleOrder ? PriceType.PLUS_MEMBER : PriceType.DEFAULT;
    const waiveFrozenFee = getWaiveFrozenFee({
        account,
        options: { priceType },
        selections: filteredSelections,
    });
    const frozenSubtotal = getProductsSubtotal({
        options: { priceType },
        products: frozenProducts,
    });
    const amountRemaining = getFrozenFeeAmountRemaining({
        account,
        options: { priceType },
        selections: filteredSelections,
    });

    const progress =
        frozenFeeWaiveThreshold > 0 ? Math.min(100, (frozenSubtotal / frozenFeeWaiveThreshold) * 100) : 100;

    if (waiveFrozenFee) {
        return (
            <div className={clsx({ 'mt-10 border-t border-grey-light pt-10': stacked })}>
                <p className="text-body-sm text-brand-primary font-grotesk-bold">Your frozen fee is waived!</p>
            </div>
        );
    }

    return (
        <div className={clsx({ 'mt-10 border-t border-grey-light pt-10': stacked })}>
            <div className="mt-5 flex items-center justify-between">
                <div className="mr-30 grow">
                    <Progress color="grey" progress={progress} width="full" />
                </div>
                <p className="text-body-sm font-grotesk-bold">
                    <span className="text-error">${frozenSubtotal.toFixed(2)} </span> / $
                    {frozenFeeWaiveThreshold.toFixed(2)} frozen
                </p>
            </div>
            <p className="text-body-sm">
                Add <span className="font-grotesk-bold text-error">${amountRemaining.toFixed(2)}</span> of frozen items
                to waive Frozen Fee.
            </p>
        </div>
    );
}

export default FrozenFeeWaiveProgress;
